<script setup lang="ts">
import type { ProductVariant } from '~/types/Product';
import { useCurrency } from '~/stores/currency';
import { storeToRefs } from 'pinia';
import { useLoganStore } from '~/stores/logan.ts';
import CashIcon from '~/icons/CashIcon.vue';
import { isOriginalProductPrice } from '~/utils/helpers.ts';
import type { ProductSchema } from '@laam/cms-shared';
import { INVENTORY } from '~/utils/constants';

interface ProductPriceProps {
	selectedVariantData: ProductVariant | null;
	productData: ProductSchema;
}

const props = defineProps<ProductPriceProps>();
const LoganStore = useLoganStore();

const { isSignedIn, isHvc } = storeToRefs(LoganStore);

const rewardsEnabled: Ref<boolean | undefined> = inject('rewardsEnabled')!;
const productRewardsAmount: Ref<number> = inject('productRewardsAmount')!;
const checkoutLocalization: Ref<boolean> = inject('checkoutLocalization')!;

const isOnSale = computed(() => {
	if (props.selectedVariantData) {
		return (
			parseInt(props.selectedVariantData.compare_at_price) >
			parseInt(props.selectedVariantData.price)
		);
	} else {
		return props.productData.compare_at_price > props.productData.price;
	}
});
const cheapestVariant = computed(() =>
	props.productData.variants.reduce((prev, current) =>
		parseFloat(prev.price) < parseFloat(current.price) ? prev : current,
	),
);
const showHvcDiscount = computed(() => {
	if (props.selectedVariantData) {
		if (props.selectedVariantData.reseller_price) return true;
		else return false;
	} else {
		if (cheapestVariant.value.reseller_price) return true;
		else return false;
	}
});

const hvcPrice = computed(() => {
	if (props.selectedVariantData) {
		return parseFloat(props.selectedVariantData.reseller_price!);
	} else {
		return cheapestVariant.value.reseller_price;
	}
});

const price = computed(() => {
	if (props.selectedVariantData) {
		return parseFloat(props.selectedVariantData.price);
	} else {
		return props.productData.price;
	}
});

const compareAtPrice = computed(() => {
	if (props.selectedVariantData) {
		return parseFloat(props.selectedVariantData.compare_at_price);
	} else {
		return props.productData.compare_at_price;
	}
});

function salePercentage(
	newPrice: string | number | undefined,
	oldPrice: string | number,
) {
	const p = typeof newPrice === 'string' ? parseInt(newPrice) : newPrice;
	if (!p) return 0;
	const cap = typeof oldPrice === 'string' ? parseInt(oldPrice) : oldPrice;
	return ((cap - p + 0.5) * 100) / cap;
}

const isOriginalPrice = computed(() => {
	if (props.productData) {
		return isOriginalProductPrice(
			props.productData.compare_at_price,
			props.productData.price,
		);
	}

	return false;
});

const CurrencyStore = useCurrency();
// function priceFormatter(price: string | number) {
// 	console.log('props', props);

// 	console.log('priceFormatter', price);
// 	return price.toLocaleString();
// }

// HVC Set functionality

// import { ProductTags } from '../Enums';
// const prod_enum = ProductTags;

// const buyingPrice = computed(() => {
// 	const variantPrice = props.selectedVariantData
// 		? props.selectedVariantData.priceV2.amount
// 		: props.productData.priceRange.minVariantPrice.amount;
// 	return props.selectedVariantData && props.selectedVariantData.new_price !== undefined
// 		? props.selectedVariantData.new_price
// 		: variantPrice;
// });

// function metafieldValue(index: number) {
// 	if (props.productData.metafields.length > 2 && props.productData.metafields[index]) {
// 		return props.productData.metafields[index].value;
// 	} else {
// 		return '';
// 	}
// }

// const avgRetailPrice = computed(
// 	() =>
// 		currencyCode.value +
// 		priceFormatter(buyingPrice.value / productPieces.value),
// );

// const productPieces = computed(() => {
// 	return props.productData.tags.includes('hvc_set') ? Number(metafieldValue(5)) : 1;
// });
</script>

<template>
	<div class="pdp__price gap-md flex flex-col">
		<div class="gap-xs flex flex-col">
			<div v-if="price" class="price">
				<span class="price-currency">
					{{ CurrencyStore.currencySymbol }}{{ ' ' }}
				</span>
				<span class="price-amount font-semibold">
					{{
						showHvcDiscount && props.productData.stock_status != INVENTORY.OOS
							? CurrencyStore.getFormattedPrice(hvcPrice!, true)
							: CurrencyStore.getFormattedPrice(price, !isOriginalPrice)
					}}
				</span>
			</div>
			<div v-if="showHvcDiscount || isOnSale" class="gap-md flex items-center">
				<div
					v-if="
						showHvcDiscount && props.productData.stock_status != INVENTORY.OOS
					"
					class="gap-xs flex items-center"
				>
					<span class="is-hvc-price">
						<span class="is-hvc-price-currency">
							{{ CurrencyStore.currencySymbol }}{{ ' ' }}
						</span>
						&nbsp;
						<span class="is-hvc-price-amount">{{
							CurrencyStore.getFormattedPrice(price, !isOriginalPrice)
						}}</span>
					</span>
					<span class="hvc-percentage">
						Extra -{{ salePercentage(hvcPrice, price).toFixed(0) }}%
					</span>
				</div>

				<div v-if="isOnSale" class="gap-xs flex items-center">
					<div class="compare-price">
						<span class="compare-price-currency">
							{{ CurrencyStore.currencySymbol }}
						</span>
						&nbsp;
						<span class="compare-price-amount">
							{{
								CurrencyStore.getFormattedPrice(compareAtPrice, isOriginalPrice)
							}}
						</span>
					</div>
					<span class="sale-percentage">
						-{{ salePercentage(price, compareAtPrice).toFixed(0) }}%
					</span>
				</div>
			</div>
		</div>
		<div
			v-if="rewardsEnabled && isSignedIn && !isHvc && productRewardsAmount"
			class="gap-xl flex"
		>
			<CashIcon class="size-5xl" />
			<div class="text-sm font-normal">
				Earn
				<span v-if="checkoutLocalization" class="text-sm font-semibold">
					{{ productRewardsAmount.toLocaleString('en-US') }} points
				</span>
				<span v-else class="text-sm font-semibold">
					{{ CurrencyStore.currencySymbol }}
					{{ CurrencyStore.getFormattedPrice(productRewardsAmount) }}
				</span>
				in rewards
			</div>
		</div>
	</div>
</template>

<style scoped>
.pc-text {
	text-justify: distribute;
}

.price {
	@apply text-gray-800 text-lg;
}

.compare-price {
	@apply line-through flex text-sm font-normal tracking-[-0.6px] text-gray-600;
}

.sale-percentage {
	@apply text-sm font-semibold text-red-600;
}

.is-hvc-price {
	@apply line-through flex text-sm font-normal tracking-[-0.6px] text-gray-600;
}
.hvc-percentage {
	@apply text-sm font-semibold text-orange-600;
}
</style>
